import React, { useState, useEffect } from 'react'
import {
  Card, Button, Tag, Tabs, Table, Progress, Modal, Form,
  InputNumber, DatePicker, Select, Input, Alert, Tooltip,
  Timeline, message, Space, Empty,
} from 'antd'
import {
  ArrowLeftOutlined, PlusOutlined, EditOutlined,
  HistoryOutlined, SwapOutlined, WarningOutlined,
  CheckCircleOutlined, UserOutlined, PauseCircleOutlined,
  SendOutlined, CloseCircleOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { mappingStore, ConsumerRow, approvalOf, APPROVAL_LABEL, APPROVAL_PILL } from './mappingStore'
import {
  canApproveMappings, canSubmitForApproval, submitForApproval,
  approveMapping, rejectMapping,
} from './mappingApproval'
import { useMasterData } from '@/context/MasterDataContext'
import { authStore } from '@/context/auth'
import { annualToMonthlyUnits, formatUnitsKWh } from '@/utils/numberFormat'
import dayjs from 'dayjs'

interface TariffChange {
  date: string
  consumerId: string
  consumerName: string
  oldTariff: number
  newTariff: number
  effectiveDate: string
  reason: string
  changedBy: string
}

interface ChangeLog {
  date: string
  action: string
  detail: string
  user: string
  type: 'add' | 'remove' | 'modify' | 'suspend' | 'activate'
}

const plantTypeColor: Record<string, string> = {
  'Wind & Solar': '#2563EB', 'Solar': '#F59E0B', 'Biomass': '#059669', 'Hydro': '#0891B2',
}

const logIcon: Record<string, React.ReactNode> = {
  add:      <PlusOutlined style={{ color: '#059669' }} />,
  remove:   <UserOutlined style={{ color: '#DC2626' }} />,
  modify:   <EditOutlined style={{ color: '#2563EB' }} />,
  suspend:  <PauseCircleOutlined style={{ color: '#F59E0B' }} />,
  activate: <CheckCircleOutlined style={{ color: '#059669' }} />,
}

const MappingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [reviseTariffModal, setReviseTariffModal] = useState(false)
  const [addConsumerModal, setAddConsumerModal]   = useState(false)
  const [revisingTariff, setRevisingTariff]       = useState(false)
  const [addingConsumer, setAddingConsumer]       = useState(false)
  const [selectedConsumer, setSelectedConsumer]   = useState<ConsumerRow | null>(null)
  const [tariffForm] = Form.useForm()
  const [addForm]    = Form.useForm()
  const [, setTick]  = useState(0)
  const [rejectModal, setRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const { consumers: masterConsumers, roles } = useMasterData()

  useEffect(() => {
    const bump = () => setTick(t => t + 1)
    mappingStore.refresh().then(bump)
    window.addEventListener(mappingStore.eventName, bump)
    return () => window.removeEventListener(mappingStore.eventName, bump)
  }, [])

  const stored = id ? mappingStore.get(id) : undefined

  if (!stored) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Empty description="Mapping group not found" />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/mappings')}>Back to Mappings</Button>
      </div>
    )
  }

  const mapping = {
    id: stored.id,
    generatorId: stored.generatorId,
    generatorName: stored.generatorName,
    plantType: stored.plantType,
    capacity: stored.capacity,
    state: '',
    consumers: stored.consumers || [],
    totalAllocated: stored.totalAllocated || 0,
    status: stored.status,
    tariffHistory: (stored as any).tariffHistory || [] as TariffChange[],
    changeLog: (stored as any).changeLog || [] as ChangeLog[],
  }

  const isBalanced = Math.round(Number(mapping.totalAllocated)) >= 100
  const isAdmin = canApproveMappings(roles)
  const approval = approvalOf(stored)

  const plant = {
    name: stored?.plantName,
    ht: stored?.htNumber,
    location: stored?.plantLocation,
    substation: stored?.substation,
    capacity: stored?.plantCapacity,
  }
  const totalAllocatedUnits = (mapping.consumers || []).reduce((s, c) => s + Number((c as ConsumerRow).allocatedUnits ?? 0), 0)

  const consumerAnnualCapacity = (consumerId?: string): number => {
    const consumer = masterConsumers.find(c => c.id === consumerId)
    if (!consumer) return 0
    return (consumer.services || [])
      .filter(s => s.status === 'active')
      .reduce((sum, s) => sum + Number(s.avgAnnualConsumption || 0), 0)
  }
  // Allocation ceilings are checked per month, not per annum — see ConsumerAllocationEditor.
  const consumerMonthlyCapacity = (consumerId?: string): number => Math.round(annualToMonthlyUnits(consumerAnnualCapacity(consumerId)))
  const plantMonthlyCapacity = Math.round(annualToMonthlyUnits(stored?.plantCapacity ?? 0))

  const openReviseTariff = (consumer: ConsumerRow) => {
    setSelectedConsumer(consumer)
    tariffForm.setFieldsValue({
      currentTariff: consumer.tariff,
      newTariff: consumer.tariff,
      effectiveDate: dayjs(),
      reason: '',
    })
    setReviseTariffModal(true)
  }

  const handleReviseTariff = () => {
    setRevisingTariff(true)
    tariffForm.validateFields().then(vals => {
      if (!selectedConsumer || !stored) return
      const oldRate = Number(selectedConsumer.tariff) || 0
      const newRate = Number(vals.newTariff) || 0

      const updatedConsumers = stored.consumers.map(c =>
        c.consumerId === selectedConsumer.consumerId ? { ...c, tariff: newRate } : c
      )

      const historyEntry: TariffChange = {
        date: dayjs().format('YYYY-MM-DD'),
        consumerId: selectedConsumer.consumerId,
        consumerName: selectedConsumer.consumerName,
        oldTariff: oldRate,
        newTariff: newRate,
        effectiveDate: vals.effectiveDate ? vals.effectiveDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        reason: vals.reason || 'Tariff Revised',
        changedBy: authStore.getUser()?.name || 'Admin',
      }

      const logEntry: ChangeLog = {
        date: dayjs().format('YYYY-MM-DD HH:mm'),
        action: 'Tariff Revised',
        detail: `Revised tariff for ${selectedConsumer.consumerName} from ₹${oldRate.toFixed(2)} to ₹${newRate.toFixed(2)}`,
        user: authStore.getUser()?.name || 'Admin',
        type: 'modify',
      }

      const existingHistory = (stored as any).tariffHistory || []
      const existingLog = (stored as any).changeLog || []

      mappingStore.save({
        id: stored.id,
        consumers: updatedConsumers,
        tariffHistory: [...existingHistory, historyEntry],
        changeLog: [...existingLog, logEntry],
      } as any)

      setTick(t => t + 1)
      message.success(`Tariff revised for ${selectedConsumer.consumerName}. History updated.`)
      setReviseTariffModal(false)
      tariffForm.resetFields()
    }).catch(() => {
      message.error('Please fill in all required tariff fields.')
    }).finally(() => {
      setRevisingTariff(false)
    })
  }

  const handleToggleSuspend = (consumer: ConsumerRow) => {
    if (!stored) return
    const nextStatus = consumer.status === 'active' ? 'suspended' : 'active'
    const updatedConsumers = stored.consumers.map(c =>
      c.consumerId === consumer.consumerId ? { ...c, status: nextStatus } : c
    )

    const logEntry: ChangeLog = {
      date: dayjs().format('YYYY-MM-DD HH:mm'),
      action: nextStatus === 'suspended' ? 'Consumer Suspended' : 'Consumer Reactivated',
      detail: `${consumer.consumerName} set to ${nextStatus}. Allocation status updated.`,
      user: authStore.getUser()?.name || 'Admin',
      type: nextStatus === 'suspended' ? 'suspend' : 'activate',
    }

    const existingLog = (stored as any).changeLog || []

    mappingStore.save({
      id: stored.id,
      consumers: updatedConsumers,
      changeLog: [...existingLog, logEntry],
    } as any)

    setTick(t => t + 1)
    message.success(`${consumer.consumerName} status set to ${nextStatus}.`)
  }

  const handleAddConsumer = () => {
    setAddingConsumer(true)
    addForm.validateFields().then(vals => {
      const cap = plantMonthlyCapacity
      const existingUnits = (stored?.consumers ?? []).reduce((s, c) => s + Number(c.allocatedUnits ?? 0), 0)
      if (cap > 0 && existingUnits + Number(vals.allocatedUnits || 0) > cap) {
        message.error(`Allocated units would exceed the plant's proposed monthly generation (${Math.round(cap).toLocaleString('en-IN')} kWh/mo).`); return
      }
      const consumerCap = consumerMonthlyCapacity(vals.consumerId)
      if (consumerCap > 0 && Number(vals.allocatedUnits || 0) > consumerCap) {
        message.error(`Allocated units exceed this consumer's Avg Monthly Consumption (~${Math.round(consumerCap).toLocaleString('en-IN')} kWh/mo).`); return
      }
      const masterC = masterConsumers.find(c => c.id === vals.consumerId)
      const row: ConsumerRow = {
        consumerId: vals.consumerId,
        consumerName: masterC?.name ?? vals.consumerId,
        shareHolding: Number(vals.shareHolding) || 0,
        allocatedUnits: Number(vals.allocatedUnits) || 0,
        effectiveFrom: vals.effectiveFrom ? vals.effectiveFrom.format('YYYY-MM-DD') : '',
        effectiveTo: vals.effectiveTo ? vals.effectiveTo.format('YYYY-MM-DD') : '',
        tariff: Number(vals.tariff) || 0,
        tnebTariff: 0,
        tradeMargin: 0,
        status: 'active',
      }
      if (stored) {
        const consumers = [...stored.consumers, row]
        const logEntry: ChangeLog = {
          date: dayjs().format('YYYY-MM-DD HH:mm'),
          action: 'Consumer Added',
          detail: `Added ${row.consumerName} (${row.shareHolding}% allocation, ${(row.allocatedUnits || 0).toLocaleString('en-IN')} kWh/yr)`,
          user: authStore.getUser()?.name || 'Admin',
          type: 'add',
        }
        const existingLog = (stored as any).changeLog || []
        mappingStore.save({
          id: stored.id,
          consumers,
          totalAllocated: consumers.reduce((s, c) => s + Number(c.shareHolding), 0),
          changeLog: [...existingLog, logEntry],
          lastUpdated: new Date().toISOString().slice(0, 10),
        } as any)
        setTick(t => t + 1)
        message.success(`${row.consumerName} mapped to this plant group.`)
      } else {
        message.success('Consumer added to mapping.')
      }
      setAddConsumerModal(false)
      addForm.resetFields()
    }).catch(() => {
      message.error('Please fill in all required fields.')
    }).finally(() => {
      setAddingConsumer(false)
    })
  }

  /* ── Consumer Allocation Tab ─────────────────────────── */
  const consumerColumns = [
    {
      title: 'Consumer',
      key: 'consumer',
      render: (_: any, r: ConsumerRow) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0B1426' }}>{r.consumerName}</div>
          <div style={{ fontSize: 11, color: '#8A93A6', fontFamily: 'JetBrains Mono, monospace' }}>{r.consumerId}</div>
        </div>
      ),
    },
    {
      title: 'Allocation %',
      dataIndex: 'shareHolding',
      render: (v: number) => (
        <div>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 15, color: '#001A4D' }}>{v}%</div>
          <Progress percent={v} showInfo={false} strokeColor="#001A4D" trailColor="#E4E8F0" size="small" style={{ margin: '4px 0 0' }} />
        </div>
      ),
    },
    {
      title: 'Allocated Units',
      dataIndex: 'allocatedUnits',
      render: (v: number) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 14, color: '#2563EB' }}>
          {v != null ? Number(v).toLocaleString('en-IN') : '—'}
        </span>
      ),
    },
    {
      title: 'Effective',
      key: 'effective',
      render: (_: any, r: ConsumerRow) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#4A5568' }}>
          {r.effectiveFrom || '—'}{r.effectiveTo ? ` → ${r.effectiveTo}` : ''}
        </span>
      ),
    },
    {
      title: 'Tariff (₹/kWh)',
      dataIndex: 'tariff',
      render: (v: number) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 14, color: '#7C3AED' }}>₹{(Number(v) || 0).toFixed(2)}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: string) => s === 'active'
        ? <span className="pill pill-green">Active</span>
        : <span className="pill pill-energy">Suspended</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, r: ConsumerRow) => (
        <Space size={4}>
          <Button size="small" icon={<HistoryOutlined />} onClick={() => openReviseTariff(r)}>
            Revise Tariff
          </Button>
          <Tooltip title={r.status === 'active' ? 'Suspend Consumer' : 'Reactivate Consumer'}>
            <Button
              size="small"
              danger={r.status === 'active'}
              icon={r.status === 'active' ? <PauseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleToggleSuspend(r)}
            >
              {r.status === 'active' ? 'Suspend' : 'Reactivate'}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ]

  /* ── Tariff History Tab ──────────────────────────────── */
  const historyColumns = [
    { title: 'Date', dataIndex: 'date', render: (d: string) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{d}</span> },
    { title: 'Consumer', dataIndex: 'consumerName', render: (n: string) => <span style={{ fontWeight: 600 }}>{n}</span> },
    {
      title: 'Old Tariff',
      dataIndex: 'oldTariff',
      render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#DC2626' }}>₹{v.toFixed(2)}</span>,
    },
    {
      title: 'New Tariff',
      dataIndex: 'newTariff',
      render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#059669', fontWeight: 700 }}>₹{v.toFixed(2)}</span>,
    },
    {
      title: 'Effective Date',
      dataIndex: 'effectiveDate',
      render: (d: string) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{d}</span>,
    },
    { title: 'Reason', dataIndex: 'reason', render: (r: string) => <span style={{ color: '#4A5568' }}>{r}</span> },
    { title: 'Changed By', dataIndex: 'changedBy', render: (u: string) => <Tag>{u}</Tag> },
  ]

  const tabItems = [
    {
      key: 'consumers',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <SwapOutlined />Consumer Allocation
        </span>
      ),
      children: (
        <div>
          {/* Allocation summary bar */}
          <div style={{
            background: isBalanced ? '#D1FAE5' : '#FEF3C7',
            border: `1px solid ${isBalanced ? '#6EE7B7' : '#FDE68A'}`,
            borderRadius: 10,
            padding: '12px 18px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {isBalanced
                ? <CheckCircleOutlined style={{ color: '#059669', fontSize: 18 }} />
                : <WarningOutlined style={{ color: '#92400E', fontSize: 18 }} />
              }
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: isBalanced ? '#065F46' : '#92400E' }}>
                  {isBalanced ? 'Allocation Balanced — 100%' : `Allocation Incomplete — ${mapping.totalAllocated}% assigned`}
                </div>
                <div style={{ fontSize: 12, color: isBalanced ? '#059669' : '#B45309', marginTop: 2 }}>
                  {isBalanced
                    ? `${mapping.consumers.length} consumers sharing full generation`
                    : `${100 - mapping.totalAllocated}% unallocated — units will remain unused`}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 28, fontWeight: 700, color: isBalanced ? '#059669' : '#92400E' }}>
                {mapping.totalAllocated}%
              </div>
              <Progress
                percent={Math.min(mapping.totalAllocated, 100)}
                showInfo={false}
                strokeColor={isBalanced ? '#059669' : '#F59E0B'}
                trailColor={isBalanced ? '#A7F3D0' : '#FDE68A'}
                style={{ width: 140, margin: 0 }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <Button icon={<PlusOutlined />} onClick={() => setAddConsumerModal(true)}>
              Add Consumer
            </Button>
          </div>

          <Table
            dataSource={mapping.consumers}
            columns={consumerColumns}
            rowKey="consumerId"
            pagination={false}
            summary={() => (
              <Table.Summary.Row style={{ background: '#F1F4F9' }}>
                <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 15, color: isBalanced ? '#059669' : '#DC2626' }}>
                    {mapping.totalAllocated}%
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5} />
                <Table.Summary.Cell index={6} />
              </Table.Summary.Row>
            )}
          />
        </div>
      ),
    },
    {
      key: 'history',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <HistoryOutlined />Tariff History
        </span>
      ),
      children: (
        <div>
          {mapping.tariffHistory.length === 0
            ? <div style={{ textAlign: 'center', padding: '48px 0', color: '#8A93A6', fontSize: 14 }}>
                No tariff revisions recorded yet.
              </div>
            : <Table
                dataSource={mapping.tariffHistory.map((h: any, i: number) => ({ ...h, key: i }))}
                columns={historyColumns}
                pagination={false}
              />
          }
        </div>
      ),
    },
    {
      key: 'changelog',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <EditOutlined />Change Log
        </span>
      ),
      children: (
        <div style={{ padding: '8px 0' }}>
          <Timeline
            items={mapping.changeLog.map((log: any, i: number) => ({
              key: i,
              dot: logIcon[log.type] || <EditOutlined />,
              children: (
                <div style={{ paddingBottom: 4 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#0B1426' }}>{log.action}</span>
                    <Tag style={{ fontSize: 10, margin: 0 }}>{log.user}</Tag>
                    <span style={{ fontSize: 11, color: '#8A93A6', fontFamily: 'JetBrains Mono, monospace' }}>{log.date}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#4A5568' }}>{log.detail}</div>
                </div>
              ),
            }))}
          />
        </div>
      ),
    },
  ]

  return (
    <div>
      {/* Back + header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/mappings')} />
        <div style={{ flex: 1 }}>
          <h2 className="page-title">{mapping.generatorName}{plant.name ? ` — ${plant.name}` : ''}</h2>
          <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#8A93A6' }}>{mapping.generatorId}</span>
            {plant.ht && <Tag style={{ fontFamily: 'JetBrains Mono, monospace', margin: 0 }}>{plant.ht}</Tag>}
            <span className="pill" style={{ background: plantTypeColor[mapping.plantType] + '18', color: plantTypeColor[mapping.plantType] }}>
              {mapping.plantType}
            </span>
            <span style={{ fontSize: 12, color: '#8A93A6' }}>· {mapping.capacity} · {mapping.state}</span>
            {plant.substation && <span style={{ fontSize: 12, color: '#8A93A6' }}>· {plant.substation}</span>}
          </div>
        </div>
        <Space>
          <span className={`pill ${APPROVAL_PILL[approval]}`}>{APPROVAL_LABEL[approval]}</span>
          {canSubmitForApproval(stored) && (
            <Button icon={<SendOutlined />} onClick={() => submitForApproval(stored.id)}>
              {approval === 'rejected' ? 'Resubmit for Approval' : 'Submit for Approval'}
            </Button>
          )}
          {approval === 'pending' && isAdmin && (
            <>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                style={{ background: '#00A651', borderColor: '#00A651' }}
                onClick={() => approveMapping(stored.id)}
              >
                Approve
              </Button>
              <Button danger icon={<CloseCircleOutlined />} onClick={() => { setRejectReason(''); setRejectModal(true) }}>
                Reject
              </Button>
            </>
          )}
          <Button icon={<PlusOutlined />} onClick={() => setAddConsumerModal(true)}>Add Consumer</Button>
          <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`/mappings/${id}/edit`)}>Edit Mapping</Button>
        </Space>
      </div>

      {/* KPI row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div className="stat-card" style={{ flex: 1, minWidth: 160 }}>
          <div className="stat-label">Total Allocation</div>
          <div className="stat-value" style={{ color: isBalanced ? '#059669' : '#92400E' }}>{mapping.totalAllocated}%</div>
          <div className="stat-meta">{isBalanced ? 'Balanced' : 'Unbalanced'}</div>
        </div>
        <div className="stat-card green" style={{ flex: 1, minWidth: 160 }}>
          <div className="stat-label">Active Consumers</div>
          <div className="stat-value" style={{ color: '#059669' }}>
            {mapping.consumers.filter(c => c.status === 'active').length}
          </div>
          <div className="stat-meta">of {mapping.consumers.length} total</div>
        </div>
        <div className="stat-card" style={{ flex: 1, minWidth: 160, borderLeftColor: '#2563EB' }}>
          <div className="stat-label">Allocated Units / mo</div>
          <div className="stat-value" style={{ color: '#2563EB' }}>{totalAllocatedUnits.toLocaleString('en-IN')}</div>
          <div className="stat-meta">of {plantMonthlyCapacity ? `~${formatUnitsKWh(plantMonthlyCapacity)}/mo proposed monthly generation` : '—'}</div>
        </div>
        <div className="stat-card energy" style={{ flex: 1, minWidth: 160 }}>
          <div className="stat-label">Suspended</div>
          <div className="stat-value" style={{ color: '#92400E' }}>
            {mapping.consumers.filter(c => c.status === 'suspended').length}
          </div>
          <div className="stat-meta">allocation adjusted</div>
        </div>
      </div>

      {approval !== 'approved' && (
        <Alert
          type={approval === 'rejected' ? 'error' : approval === 'pending' ? 'info' : 'warning'}
          showIcon
          icon={<SafetyCertificateOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={
            approval === 'pending' ? 'Awaiting admin approval'
              : approval === 'rejected' ? 'Mapping rejected'
              : 'Draft — not yet submitted for approval'
          }
          description={
            approval === 'pending'
              ? `Submitted by ${stored.submittedBy || '—'}${stored.submittedAt ? ` on ${dayjs(stored.submittedAt).format('DD MMM YYYY, HH:mm')}` : ''}. Only an Admin can approve or reject this mapping.`
              : approval === 'rejected'
                ? `Rejected by ${stored.approvedBy || '—'}${stored.approvedAt ? ` on ${dayjs(stored.approvedAt).format('DD MMM YYYY, HH:mm')}` : ''}.${stored.approvalComments ? ` Reason: ${stored.approvalComments}` : ''} Revise the allocation and resubmit.`
                : 'This mapping has not been submitted yet. Submit it for approval when the allocation is final.'
          }
        />
      )}

      {approval === 'approved' && stored.approvedBy && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={`Approved by ${stored.approvedBy}${stored.approvedAt ? ` on ${dayjs(stored.approvedAt).format('DD MMM YYYY, HH:mm')}` : ''}`}
        />
      )}

      {!isBalanced && (
        <Alert
          type="warning"
          showIcon
          message={`Allocation Warning — ${100 - mapping.totalAllocated}% unallocated`}
          description="The total consumer share-holding is less than 100%. Unallocated units will not be distributed. Add consumers or increase existing shares to balance."
          style={{ marginBottom: 20, borderRadius: 10 }}
        />
      )}

      {/* Main tabs */}
      <Card bodyStyle={{ padding: '0 20px 20px' }}>
        <Tabs items={tabItems} defaultActiveKey="consumers" />
      </Card>

      {/* ── Reject Mapping Modal ──────────────────────────── */}
      <Modal
        title="Reject Mapping"
        open={rejectModal}
        onCancel={() => setRejectModal(false)}
        okText="Reject Mapping"
        okButtonProps={{ danger: true }}
        onOk={() => { if (rejectMapping(stored.id, rejectReason)) setRejectModal(false) }}
      >
        <p style={{ marginBottom: 8 }}>
          Why is this generator–consumer mapping being rejected? The reason is shown to whoever submitted it.
        </p>
        <Input.TextArea
          rows={3}
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="e.g. Allocation exceeds the consumer's sanctioned demand"
        />
      </Modal>

      {/* ── Revise Tariff Modal ───────────────────────────── */}
      <Modal
        open={reviseTariffModal}
        onCancel={() => setReviseTariffModal(false)}
        onOk={handleReviseTariff}
        confirmLoading={revisingTariff}
        title={`Revise Tariff — ${selectedConsumer?.consumerName}`}
        okText="Save Revision"
        width={520}
      >
        <Alert
          type="info"
          showIcon
          message="Mid-month change handling"
          description="Choose the effective date carefully. For mid-month changes, the system will prorate allocation for the affected billing period (M-02). Confirm the handling with the customer before saving."
          style={{ marginBottom: 20, borderRadius: 8 }}
        />
        <Form form={tariffForm} layout="vertical">
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item label="Current Sakthi Tariff (₹/kWh)" name="currentTariff" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} disabled prefix="₹" />
            </Form.Item>
            <Form.Item
              label="New Sakthi Tariff (₹/kWh)"
              name="newTariff"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} prefix="₹" precision={2} />
            </Form.Item>
          </div>
          <Form.Item
            label="Effective Date"
            name="effectiveDate"
            rules={[{ required: true, message: 'Required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            label="Reason for Revision"
            name="reason"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input.TextArea rows={2} placeholder="e.g., Annual rate revision, renegotiation, regulatory update…" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Add Consumer Modal ───────────────────────────── */}
      <Modal
        open={addConsumerModal}
        onCancel={() => setAddConsumerModal(false)}
        onOk={handleAddConsumer}
        confirmLoading={addingConsumer}
        title="Add Consumer to Mapping"
        okText="Add Consumer"
        width={520}
      >
        <Form form={addForm} layout="vertical" initialValues={{ effectiveFrom: dayjs() }}>
          <Form.Item label="Consumer" name="consumerId" rules={[{ required: true }]}>
            <Select
              placeholder="Select consumer"
              showSearch optionFilterProp="label"
              options={masterConsumers
                .filter(c => c.status === 'active' && !mapping.consumers.some(mc => mc.consumerId === c.id))
                .map(c => ({ label: `${c.name} (${c.code || c.id})`, value: c.id }))}
            />
          </Form.Item>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              label="Allocation %"
              name="shareHolding"
              style={{ flex: 1 }}
              rules={[{ required: true }]}
              extra={`Remaining: ${100 - mapping.totalAllocated}%`}
            >
              <InputNumber style={{ width: '100%' }} min={1} max={100 - mapping.totalAllocated} suffix="%" />
            </Form.Item>
            <Form.Item
              label="Allocated Units / mo"
              name="allocatedUnits"
              style={{ flex: 1 }}
              dependencies={['consumerId']}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const cap = consumerMonthlyCapacity(getFieldValue('consumerId'))
                    if (!cap || !value || value <= cap) return Promise.resolve()
                    return Promise.reject(new Error(`Exceeds consumer's Avg Monthly Consumption (~${Math.round(cap).toLocaleString('en-IN')} kWh/mo)`))
                  },
                }),
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={100} />
            </Form.Item>
            <Form.Item label="Sakthi Tariff (₹/kWh)" name="tariff" style={{ flex: 1 }} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} prefix="₹" precision={2} />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item label="Effective From" name="effectiveFrom" style={{ flex: 1 }} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item label="Effective To" name="effectiveTo" style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Optional" />
            </Form.Item>
          </div>
        </Form>
        <Alert
          type="info" showIcon
          message={`Remaining allocation: ${100 - mapping.totalAllocated}%. Maximum share you can assign to this consumer.`}
          style={{ borderRadius: 8 }}
        />
      </Modal>
    </div>
  )
}

export default MappingDetail
